/**
 * A live call follows the person (docs/plans/os-live-mode.md 2.3).
 *
 * The call is told where the person is now, from the router's location, on
 * every route change, and again when the page's view changes (a filter, a
 * selection, the entry drawer), once a burst of changes has settled for
 * `LIVE_PLACE_DEBOUNCE_MS`, and when an insight's page reports its open card
 * (`watchOpenInsight`). The next question reads it; nothing is said
 * (Decided 13). A place the call was already told is not sent again.
 *
 * The view is read from the store when the place is sent and only watched
 * through a subscription, so a list that redraws never re-renders the box
 * that holds this hook.
 */
import { LIVE_PLACE_DEBOUNCE_MS, type AssistantLiveContext } from '@alluzo/shared';
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { watchOpenInsight, withOpenInsight } from '~/features/insights/os/open-insight';

import { screenCardsFor } from '@/lib/agent-screen-cards';
import { useAgentViewStore } from '@/stores/agent-view-store';

import { livePlaceOf, viewOf, type LivePlace } from './coach-screen';

type Place = Pick<AssistantLiveContext, 'screen' | 'recordId' | 'cards' | 'view'>;

/** Everything a call is told about where the person is, at this location. */
export const livePlaceContext = (tenantId: string, pathname: string, thread: LivePlace): Place => {
  const at = livePlaceOf(pathname, thread);
  const view = viewOf(at, useAgentViewStore.getState().held);
  return {
    screen: at.screen,
    ...(at.recordId === null ? {} : { recordId: at.recordId }),
    cards: withOpenInsight(screenCardsFor(tenantId, at.recordId)),
    ...(view === undefined ? {} : { view }),
  };
};

export const useLivePlace = (input: {
  readonly active: boolean;
  readonly tenantId: string;
  /** The thread's own place, for the OS page, where no page sits under OS. */
  readonly thread: () => LivePlace;
  readonly place: (next: Place) => void;
}): void => {
  const { active, tenantId, place } = input;
  const thread = useRef(input.thread);
  useEffect(() => {
    thread.current = input.thread;
  });
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // Bumped by a view change, or an insight opened or drawn, while a call is up, so the settle below runs again.
  const [viewed, setViewed] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const bump = (): void => setViewed((count) => count + 1);
    const stopView = useAgentViewStore.subscribe((next, before) => {
      if (next.held !== before.held) bump();
    });
    const stopInsight = watchOpenInsight(bump);
    return () => {
      stopView();
      stopInsight();
    };
  }, [active]);

  const sent = useRef<string | null>(null);
  useEffect(() => {
    if (!active) {
      sent.current = null;
      return undefined;
    }
    const wait = setTimeout(() => {
      const next = livePlaceContext(tenantId, pathname, thread.current());
      const key = JSON.stringify(next);
      if (key === sent.current) return;
      sent.current = key;
      place(next);
    }, LIVE_PLACE_DEBOUNCE_MS);
    return () => clearTimeout(wait);
  }, [active, tenantId, pathname, viewed, place]);
};
